import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface SettingRow {
  area_name: string;
  area_code: string;
  substation_name: string;
  device_tag: string;
  device_type: string;
  setting_name: string;
  existing_value: string | null;
  recommended_value: string | null;
  unit: string | null;
  is_modified: boolean;
  compliance_status: string | null;
  fault_ratio_percent: number | null;
}

const complianceColor = (status: string | null) => {
  if (!status) return '#888';
  switch (status.toLowerCase()) {
    case 'compliant': return '#4caf50';
    case 'non_compliant':
    case 'non-compliant': return '#f44336';
    case 'marginal': return '#ff9800';
    default: return '#888';
  }
};

const SettingsDatabase: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterArea, setFilterArea] = useState('');
  const [filterDevice, setFilterDevice] = useState('');
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!siteId) return;
    const load = async () => {
      try {
        const [siteRes, settingsRes] = await Promise.all([
          api.get(`/api/sites/${siteId}`),
          api.get(`/api/sites/${siteId}/settings-database`),
        ]);
        setSiteName(siteRes.data.name);
        setRows(settingsRes.data || []);
      } catch {
        setError('Failed to load settings database.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [siteId]);

  const areas = useMemo(() => Array.from(new Set(rows.map((r) => r.area_name))).sort(), [rows]);
  const devices = useMemo(() => {
    const filtered = filterArea ? rows.filter((r) => r.area_name === filterArea) : rows;
    return Array.from(new Set(filtered.map((r) => r.device_tag))).sort();
  }, [rows, filterArea]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterArea && r.area_name !== filterArea) return false;
      if (filterDevice && r.device_tag !== filterDevice) return false;
      if (modifiedOnly && !r.is_modified) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !r.device_tag.toLowerCase().includes(q) &&
          !r.setting_name.toLowerCase().includes(q) &&
          !r.substation_name.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [rows, filterArea, filterDevice, modifiedOnly, search]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <header style={{
        background: '#1a1a2e',
        color: '#fff',
        padding: '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate(`/site/${siteId}`)}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            padding: '7px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          ← Site Overview
        </button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{siteName || 'Protection Setting Database'}</div>
          <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>⚙️ PROTECTION SETTINGS DATABASE</div>
        </div>
      </header>

      <main style={{ flex: 1, padding: 32 }}>
        {loading && <div style={{ textAlign: 'center', color: '#888', padding: 60 }}>Loading settings...</div>}
        {error && (
          <div style={{
            background: '#fff5f5', border: '1px solid #f44336', borderRadius: 8,
            color: '#f44336', padding: '14px 18px', marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {!loading && (
          <>
            {/* Filter bar */}
            <div style={{
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              padding: '16px 20px',
              marginBottom: 24,
              display: 'flex',
              gap: 16,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <input
                type="text"
                placeholder="Search device, setting, substation..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  border: '1.5px solid #e0e0e0',
                  borderRadius: 6,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  minWidth: 240,
                  outline: 'none',
                }}
              />
              <select
                value={filterArea}
                onChange={(e) => { setFilterArea(e.target.value); setFilterDevice(''); }}
                style={{
                  border: '1.5px solid #e0e0e0',
                  borderRadius: 6,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Areas</option>
                {areas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select
                value={filterDevice}
                onChange={(e) => setFilterDevice(e.target.value)}
                style={{
                  border: '1.5px solid #e0e0e0',
                  borderRadius: 6,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Devices</option>
                {devices.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#333' }}>
                <input
                  type="checkbox"
                  checked={modifiedOnly}
                  onChange={(e) => setModifiedOnly(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                Modified only
              </label>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
                {filteredRows.length} setting{filteredRows.length !== 1 ? 's' : ''}
              </span>
            </div>

            {filteredRows.length === 0 ? (
              <div style={{ color: '#aaa', fontSize: 14 }}>No settings found matching filters.</div>
            ) : (
              <div style={{
                background: '#fff',
                borderRadius: 10,
                boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
                overflow: 'auto',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#0f3460', color: '#fff' }}>
                      {['Area', 'Substation', 'Device Tag', 'Type', 'Setting Name', 'Existing Value', 'Recommended', 'Unit', 'Modified', 'Compliance', 'Fault Ratio %'].map((h) => (
                        <th key={h} style={{
                          padding: '11px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          fontSize: 11,
                          letterSpacing: 0.5,
                          whiteSpace: 'nowrap',
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, i) => (
                      <tr
                        key={i}
                        style={{
                          background: r.is_modified
                            ? '#fff8e1'
                            : i % 2 === 0 ? '#fff' : '#f8f9fc',
                        }}
                      >
                        <td style={{ padding: '9px 12px', color: '#555' }}>
                          <span style={{ fontWeight: 600 }}>{r.area_code}</span>
                          <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>{r.area_name}</span>
                        </td>
                        <td style={{ padding: '9px 12px', color: '#555', whiteSpace: 'nowrap' }}>{r.substation_name}</td>
                        <td style={{ padding: '9px 12px', fontWeight: 700, color: '#0f3460', whiteSpace: 'nowrap' }}>{r.device_tag}</td>
                        <td style={{ padding: '9px 12px', color: '#666', fontSize: 11 }}>{r.device_type}</td>
                        <td style={{ padding: '9px 12px', color: '#333', fontWeight: 500 }}>{r.setting_name}</td>
                        <td style={{ padding: '9px 12px', color: '#333', fontFamily: 'monospace' }}>{r.existing_value ?? '—'}</td>
                        <td style={{ padding: '9px 12px', color: '#1a7a1a', fontFamily: 'monospace', fontWeight: r.is_modified ? 700 : 400 }}>
                          {r.recommended_value ?? '—'}
                        </td>
                        <td style={{ padding: '9px 12px', color: '#888', fontSize: 11 }}>{r.unit ?? ''}</td>
                        <td style={{ padding: '9px 12px' }}>
                          {r.is_modified ? (
                            <span style={{
                              background: '#ff980022',
                              color: '#ff9800',
                              border: '1px solid #ff980044',
                              borderRadius: 20,
                              padding: '2px 8px',
                              fontSize: 10,
                              fontWeight: 700,
                            }}>
                              YES
                            </span>
                          ) : (
                            <span style={{ color: '#bbb', fontSize: 11 }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '9px 12px' }}>
                          {r.compliance_status ? (
                            <span style={{
                              background: complianceColor(r.compliance_status) + '22',
                              color: complianceColor(r.compliance_status),
                              border: `1px solid ${complianceColor(r.compliance_status)}44`,
                              borderRadius: 20,
                              padding: '2px 8px',
                              fontSize: 10,
                              fontWeight: 600,
                              textTransform: 'capitalize',
                            }}>
                              {r.compliance_status.replace(/_/g, ' ')}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '9px 12px', color: '#555', textAlign: 'right' }}>
                          {r.fault_ratio_percent !== null && r.fault_ratio_percent !== undefined
                            ? `${r.fault_ratio_percent.toFixed(1)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      <footer style={{
        background: '#1a1a2e',
        color: '#8899aa',
        textAlign: 'center',
        padding: '14px',
        fontSize: 12,
        flexShrink: 0,
      }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

export default SettingsDatabase;
